import type { SecondaryQuotesPersistedTabContent } from 'apiConnector/tabs/secondaryMarket.tabs.model';
import type { DynamicFormResult } from 'models/forms/DynamicFormResult';
import { EssentialFormResult } from 'models/forms/essential';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ProductVariationValidSyntax } from 'models/productsReferential';
import { Wrapper } from 'models/wrapper';
import type { Observable } from 'rxjs';
import { BatchTabContentFormDetails } from 'state/workspaces/batch/tabContent/tabContent.model';
import {
  isSecondaryMarketQuotesTabType,
  type SecondaryMarketPostEventTabType,
  type TabType,
} from 'state/workspaces/model';
import { SmartQuotePersistedTabContent } from 'state/workspaces/smart-quote/model';
import type { WorkspaceTab } from 'state/workspaces/tabs/tabs.model';

export enum ApiVersion {
  V1,
  V2,
}

export interface ITabsGetAllApi {
  getAllTabDescriptions(): Observable<PersistedTabsDescription>;
}

export interface ITabsGetTabContentApi {
  getTabContent<T = PersistedTabContent>(tabId: string, apiVersion?: ApiVersion): Observable<T>;
}

export interface ITabsUpdateTabContentApi {
  updateTabContent(tabId: string, tabContent: PersistedTabContent): Observable<void>;
}

export interface ITabsUpdateTabsApi {
  updateTabDescriptions(tabsDescription: PersistedTabsDescription): Observable<void>;
}

export interface ITabsClearAllTabsApi {
  clearAllTabDescriptions(): Observable<void>;
}

export interface ITabsClearTabContentApi {
  clearTabContent(tabId: string): Observable<void>;
}

type PrimaryPersistedTabInfo = Readonly<{
  tabContentType: 'primary';
  quoteId?: string;
}>;
type PrimaryPersistedTab = WorkspaceTab & PrimaryPersistedTabInfo;

export type EssentialPersistedTab = PrimaryPersistedTab & {
  workspace: 'essential';
  isUnderlyingsResultExpanded?: boolean;
  isPayoffGraphHidden?: boolean;
  productVariation?: ProductVariationValidSyntax;
};

export function isPrimaryPersistedTab(tab: PersistedTab): tab is PrimaryPersistedTab {
  return tab.tabContentType === 'primary';
}
export function isEssentialPersistedTab(tab: PersistedTab | undefined): tab is EssentialPersistedTab {
  return tab?.tabContentType === 'primary' && tab?.workspace === 'essential';
}

export type PersistedTab =
  | PrimaryPersistedTab
  | EssentialPersistedTab
  | SecondaryPersistedTab
  | SecondaryPostEventPersistedTab;

export type SecondaryPostEventPersistedTab = WorkspaceTab &
  Readonly<{
    tabContentType: 'secondary-post-event';
    tabType: SecondaryMarketPostEventTabType;
    quoteId: string;
  }>;

export type SecondaryPersistedTab = WorkspaceTab &
  Readonly<{
    tabContentType: 'secondary';
    key: string;
  }>;

export function isSecondaryQuotesPersistedTab(tab: PersistedTab): tab is SecondaryPersistedTab {
  return tab.tabContentType === 'secondary' && isSecondaryMarketQuotesTabType(tab.type);
}

export function isSecondaryPostEventPersistedTab(tab: PersistedTab): tab is SecondaryPostEventPersistedTab {
  return tab.tabContentType === 'secondary-post-event';
}

export interface PersistedTabsDescription<Tab extends PersistedTab = PersistedTab> {
  activeTabId?: string;
  tabs: Tab[];
  isEssentialTabsPanelShown?: boolean;
}

export interface BatchPersistedTabContent {
  workspace: 'newBatch';
  tabId: string;
  negotiationIds: readonly string[];
  forms: readonly BatchTabContentFormDetails[];
  productType?: string;
  productSubtype?: string;
  wrapper?: string;
  tabType?: TabType;
}

export interface AddonPersistedTabContent {
  workspace: 'postTrade';
  tabType: 'addOn';
  productId: string;
  rfqId: string;
}

export interface TradeAllocationPersistedTabContent {
  workspace: 'postTrade';
  tabType: 'tradeAllocation';
  tradeId: string;
  product: string;
  wrapper: Wrapper;
  isin?: string;
  fileCode?: string;
  productId: string;
  rfqId: string;
}

export interface ProductModificationPersistedTabContent {
  workspace: 'postTrade';
  tabType: 'productModification';
  productId: string;
}

export type DynamicPersistedTabContent = {
  workspace: 'dynamic';
} & DynamicFormResult;

export type StaticPersistedTabContent = {
  workspace: 'static';
} & StaticFormResult;

export type EssentialPersistedTabContent = {
  workspace: 'essential';
} & EssentialFormResult;

export interface OrdersBasketPersistedTabContent {
  workspace: 'ordersBasket';
}

export type PersistedTabContent =
  | AddonPersistedTabContent
  | BatchPersistedTabContent
  | DynamicPersistedTabContent
  | StaticPersistedTabContent
  | OrdersBasketPersistedTabContent
  | ProductModificationPersistedTabContent
  | TradeAllocationPersistedTabContent
  | SecondaryQuotesPersistedTabContent
  | EssentialPersistedTabContent
  | SmartQuotePersistedTabContent;

export function isStaticPersistedTabContent(tabContent: PersistedTabContent): tabContent is StaticPersistedTabContent {
  return tabContent.workspace === 'static';
}

export function isEssentialPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is EssentialPersistedTabContent {
  return tabContent.workspace === 'essential';
}
export function isDynamicPersistedTabContent(
  tabContent: PersistedTabContent,
): tabContent is DynamicPersistedTabContent {
  return tabContent.workspace === 'dynamic';
}

export type ITabsApi = ITabsGetAllApi &
  ITabsUpdateTabsApi &
  ITabsClearAllTabsApi &
  ITabsGetTabContentApi &
  ITabsUpdateTabContentApi &
  ITabsClearTabContentApi;

export type TabsApiNamespace =
  | 'batch-screen'
  | 'batch-screen'
  | 'static-screen'
  | 'dynamic-screen'
  | 'post-trade'
  | 'orders-basket'
  | 'secondary-market-quotes'
  | 'secondary-market-batch'
  | 'secondary-market-post-trade'
  | 'secondary-market-post-events'
  | 'essential'
  | 'smart-quote';
